import React, { useState, useEffect, SyntheticEvent } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";

import Header from "./Header";
import EditWindow from "./EditWindow";
import { RootState } from "../../store/rootReducer";
import { DomSnapshot, WorkflowPayload } from "../../declarations/interface";
import SnapshotView from "../../components/SnapshotView";
import { actions as flowActions } from "../../slices/flow";
import {
  createWorkflow,
  actions as workflowActions,
} from "../../slices/workflow";
import {
  getRecordingById,
  updateRecordingName,
  getDomList,
  reloadScreenshots,
  actions as recordingActions,
} from "../../slices/recording";
import { getProjectById } from "../../slices/project";
import SuccessIcon from "../../assets/images/success-tick.svg";
import AppModal from "../../components/AppModal";
import LinkContainer from "../../components/LinkContainer";
import { convertMsToSec, generateFlowUrl } from "../../util/UtilService";
import useSnackbar from "../../hooks/useSnackbar";

const useStyles = makeStyles((theme) => ({
  body: {
    borderTop: `0.5px solid ${theme.palette.primary40.main}`,
  },
  contentCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
}));

const RecordingDetail = () => {
  const classes = useStyles();
  const { recordingId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("SNAPSHOTS");
  const [createMode, setCreateMode] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const { notify, hideNotification } = useSnackbar();

  const newFlow = useSelector((state: RootState) => state.flow);
  const {
    recording,
    dom_lists,
    status: recordingStatus,
  } = useSelector((state: RootState) => state.recording);
  const { project } = useSelector((state: RootState) => state.project);
  const { status: workflowStatus, workflow } = useSelector(
    (state: RootState) => state.workflow
  );
  const dispatch = useDispatch();

  const handleCreateMode = (mode: number) => {
    setCreateMode(mode);
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    dispatch(workflowActions.clearStatus());
    setModalOpen(false);
    handleCreateMode(0);
  };

  const handleSnackbarClose = () => {
    dispatch(workflowActions.clearStatus());
    dispatch(recordingActions.clearStatus());
    hideNotification();
  };

  const handleCreateFlowClick = () => {
    handleCreateMode(1);
  };
  const handleRecordingNameUpdate = (name: string) => {
    dispatch(updateRecordingName({ name, id: recordingId }));
  };
  const handleFlowNameUpdate = (updatedName: string) => {
    dispatch(flowActions.updateFlowName(updatedName));
  };
  const handleTabChange = (_: SyntheticEvent<Element, Event>, tab: string) => {
    const params = { tab: "1" };
    if (tab === "FLOWS") params.tab = "2";
    setSearchParams(params);
  };
  const getCount = () => {
    if (!dom_lists) return "0";
    const total = dom_lists.total;
    const count = newFlow.dom_lists.length;
    return `${count} of ${total}`;
  };
  const handleCancel = () => {
    handleCreateMode(0);
  };
  const handleNext = () => {
    if (createMode === 1) {
      handleCreateMode(2);
    } else if (createMode === 2) {
      // TODO: Create flow
      const workflow_sequences = newFlow.dom_lists
        .filter((dom) => dom.checked)
        .map((dom, index) => ({
          dom_uuid: dom.dom_uuid || dom.snapshot_id,
          dom_position: index + 1,
          wait_type: dom.wait_type,
          delay_interval: dom.delay_interval,
          event_id: dom.event_id,
          checked: dom.checked,
          api_key: dom.api_key,
          download_key: dom.download_key,
          upload_enabled: dom.upload_enabled,
          dom_modifications: dom.dom_modifications,
        }));
      const payload: WorkflowPayload = {
        recording_id: newFlow.recording_id,
        project_id: newFlow.project_id,
        workflow_name: newFlow.workflow_name,
        workflow_sequences: workflow_sequences,
        recording_type: dom_lists?.recordingType,
        allow_loop: newFlow.allow_loop,
      };
      dispatch(createWorkflow(payload));
    }
  };
  const handleBack = () => {
    handleCreateMode(1);
  };

  const fetchNextDoms = () => {
    console.log("fetching next doms");
    if (!dom_lists) return;
    if (
      dom_lists.total > dom_lists.page * dom_lists.limit &&
      createMode !== 2
    ) {
      dispatch(getDomList({ id: recordingId, page: +dom_lists.page + 1 }));
    }
  };

  const handleReloadScreenshots = () => {
    dispatch(reloadScreenshots({ id: recordingId }));
  };

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "1") {
      setActiveTab("SNAPSHOTS");
    } else if (tab === "2") {
      setActiveTab("FLOWS");
    }
  }, [searchParams, setActiveTab]);

  useEffect(() => {
    if (!recordingId) return;
    dispatch(getRecordingById(recordingId));
  }, [recordingId]);

  // Used to create new flow
  useEffect(() => {
    if (!recording) return;
    if (!project) {
      dispatch(getProjectById(recording.project_id));
      return;
    }
    dispatch(getDomList({ id: recordingId, page: 1 }));
    const initialState = {
      recording_id: recording.recording_id,
      project_id: recording.project_id,
      workflow_name: `Flow ${project.workflow_lists.length}`,
      allow_loop: true,
    };
    dispatch(flowActions.loadSnapshots(initialState));
  }, [recording, project]);

  // Used to add doms to the created flow
  useEffect(() => {
    if (!dom_lists) return;
    const transformedList = dom_lists.data.map(
      (snapshot: DomSnapshot, index: number, array: DomSnapshot[]) => {
        let delay_interval = "0";
        if (index < array.length - 1) {
          delay_interval = `100`;
        }
        const event_id =
          snapshot.events_lists.length > 0
            ? snapshot.events_lists[snapshot.events_lists.length - 1].eventId
            : undefined;
        return {
          ...snapshot,
          wait_type: snapshot.events_lists.length > 0 ? "EVENT" : "TIME",
          checked: false,
          display_delay_interval: convertMsToSec(delay_interval),
          delay_interval: delay_interval,
          event_id,
        };
      }
    );
    dispatch(
      flowActions.concatDomLists({
        append: dom_lists.page !== 1,
        list: transformedList,
      })
    );
  }, [dom_lists]);

  useEffect(() => {
    if (workflowStatus) {
      if (workflowStatus.message.includes("Workflow created successfully")) {
        handleModalOpen();
      } else {
        notify({
          message: workflowStatus.message,
          type: workflowStatus.type,
          onClose: handleSnackbarClose,
        });
      }
    }
  }, [workflowStatus]);

  useEffect(() => {
    if (recordingStatus) {
      notify({
        message: recordingStatus.message,
        type: recordingStatus.type,
        onClose: handleSnackbarClose,
      });
    }
  }, [recordingStatus]);

  return (
    <Box height="100%">
      {recording && (
        <Header
          projectId={recording.project_id}
          projectName={project?.project_name ?? "PROJECT_NAME"}
          recordingName={recording.recording_name}
          onRecordingNameUpdate={handleRecordingNameUpdate}
          onFlowNameUpdate={handleFlowNameUpdate}
          count={getCount()}
          activeTab={activeTab}
          handleTabChange={handleTabChange}
          createMode={createMode}
          handleCreateFlowClick={handleCreateFlowClick}
          onBack={handleBack}
          handleReload={handleReloadScreenshots}
        />
      )}
      {recording && (
        <Box
          className={classes.body}
          sx={{
            height: `calc(100vh - ${createMode === 2 ? 78 : 116}px)`,
          }}
        >
          {activeTab === "SNAPSHOTS" && newFlow.dom_lists.length > 0 ? (
            <Box height="100%">
              <SnapshotView
                createMode={createMode}
                editWindowVisible={createMode}
                recordingType={
                  dom_lists?.recordingType ? dom_lists?.recordingType : 0
                }
                viewType={
                  createMode === 2 ? "CREATE_FLOW_VIEW" : "RECORDING_VIEW"
                }
                fetchNext={fetchNextDoms}
                hasMore={
                  !!(
                    dom_lists &&
                    dom_lists.total > dom_lists.page * dom_lists.limit
                  ) && createMode !== 2
                }
                showAll={createMode !== 2}
              />
              {createMode !== 0 && (
                <EditWindow
                  onCancel={handleCancel}
                  onNext={handleNext}
                  onBack={handleBack}
                  createMode={createMode}
                />
              )}
            </Box>
          ) : (
            <Box className={classes.contentCenter}>
              <Box textAlign="center">
                <Typography variant="h5" color="primary">
                  Please wait...
                </Typography>
                <Typography variant="h5" color="primary">
                  All the snapshots for this recording are loading.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {workflowStatus && workflowStatus.type === "SUCCESS" && (
        <AppModal open={modalOpen} onClose={handleModalClose}>
          <Box mb={0.5}>
            <Box textAlign="center">
              <img src={SuccessIcon} alt="success" />
            </Box>
            <Typography variant="h3" sx={{ textAlign: "center" }}>
              {workflowStatus.message}
            </Typography>
            <Box display="flex" flexDirection="column" gap="8px" mt={2.75}>
              <LinkContainer
                link={
                  workflow?.workflow_entry_url ||
                  generateFlowUrl(workflow?.workflow_id ?? "")
                }
              />
            </Box>
          </Box>
        </AppModal>
      )}
    </Box>
  );
};

export default RecordingDetail;
